import { Box, Chip, TableSortLabel, Typography } from '@mui/material';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useAccount } from '../../contexts/AccountContext';
import type { Assignment } from '../../types/assignments';
import { type Column, StandardTable } from '../common/UI/StandardTable';

type Order = 'asc' | 'desc';
type OrderBy = keyof Assignment | 'assigneeName';

interface AssignmentsTableProps {
  assignments: Assignment[];
  onRowClick?: (assignment: Assignment) => void;
}

const AssignmentsTable: React.FC<AssignmentsTableProps> = ({ assignments, onRowClick }) => {
  const { account } = useAccount();
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<OrderBy>('tenderName');

  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedAssignments = useMemo(() => {
    return [...assignments].sort((a, b) => {
      let aValue = '';
      let bValue = '';

      if (orderBy === 'tenderName') {
        aValue = (a.tenderName || '').toLowerCase();
        bValue = (b.tenderName || '').toLowerCase();
      } else if (orderBy === 'sectionTitle') {
        aValue = (a.sectionTitle || '').toLowerCase();
        bValue = (b.sectionTitle || '').toLowerCase();
      } else if (orderBy === 'assigneeName') {
        aValue = (a.assignee?.displayName || '').toLowerCase();
        bValue = (b.assignee?.displayName || '').toLowerCase();
      } else if (orderBy === 'status') {
        aValue = (a.status || '').toLowerCase();
        bValue = (b.status || '').toLowerCase();
      }

      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [assignments, order, orderBy]);

  const getStatusColor = (
    status?: string
  ): 'success' | 'warning' | 'info' | 'default' | 'primary' | 'secondary' | 'error' => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'approved':
      case 'done':
        return 'success';
      case 'in_progress':
      case 'in progress':
      case 'drafting':
        return 'warning';
      case 'review':
      case 'in_review':
        return 'info';
      case 'not_started':
      case 'not started':
      case 'pending':
        return 'default';
      default:
        return 'default';
    }
  };

  const columns: Column<Assignment>[] = [
    {
      id: 'tenderName',
      label: (
        <TableSortLabel
          active={orderBy === 'tenderName'}
          direction={orderBy === 'tenderName' ? order : 'asc'}
          onClick={() => handleRequestSort('tenderName')}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            Tender Name
          </Typography>
        </TableSortLabel>
      ),
      render: (row) => {
        const isCurrentUser = account?.uid === row.assignee?.uid;
        return (
          <Typography variant="body2" fontWeight={isCurrentUser ? 'bold' : 'normal'}>
            {row.tenderName || 'N/A'}
          </Typography>
        );
      },
    },
    {
      id: 'sectionTitle',
      label: (
        <TableSortLabel
          active={orderBy === 'sectionTitle'}
          direction={orderBy === 'sectionTitle' ? order : 'asc'}
          onClick={() => handleRequestSort('sectionTitle')}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            Section Title
          </Typography>
        </TableSortLabel>
      ),
      render: (row) => {
        const isCurrentUser = account?.uid === row.assignee?.uid;
        return (
          <Typography variant="body2" fontWeight={isCurrentUser ? 'bold' : 'normal'}>
            {row.sectionTitle || 'N/A'}
          </Typography>
        );
      },
    },
    {
      id: 'assigneeName',
      label: (
        <TableSortLabel
          active={orderBy === 'assigneeName'}
          direction={orderBy === 'assigneeName' ? order : 'asc'}
          onClick={() => handleRequestSort('assigneeName')}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            Assigned To
          </Typography>
        </TableSortLabel>
      ),
      render: (row) => {
        const isCurrentUser = account?.uid === row.assignee?.uid;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" fontWeight={isCurrentUser ? 'bold' : 'normal'}>
              {row.assignee?.displayName || 'Unassigned'}
            </Typography>
            {isCurrentUser && (
              <Chip
                label="Me"
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Box>
        );
      },
    },
    {
      id: 'status',
      label: (
        <TableSortLabel
          active={orderBy === 'status'}
          direction={orderBy === 'status' ? order : 'asc'}
          onClick={() => handleRequestSort('status')}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            Status
          </Typography>
        </TableSortLabel>
      ),
      render: (row) => {
        const isCurrentUser = account?.uid === row.assignee?.uid;
        return (
          <Chip
            label={(row.status || 'Pending').replace(/_/g, ' ')}
            size="small"
            color={getStatusColor(row.status)}
            variant={isCurrentUser ? 'filled' : 'outlined'}
            sx={{ textTransform: 'capitalize' }}
          />
        );
      },
    },
  ];

  return (
    <StandardTable
      columns={columns}
      data={sortedAssignments}
      keyExtractor={(row) => row.id || ''}
      onRowClick={onRowClick}
      getRowTestId={(row) => `assignment-row-${row.id}`}
      emptyStateMessage="No assignments found"
    />
  );
};

export default AssignmentsTable;
